import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import {
  API, ACCENT, NAV_FONT, NORM, ROOD, bovenPct, haal, kleurVoor, maandLabel, pct, uren,
} from '../Declarabiliteit/Declarabiliteit';

/**
 * Declarabiliteit in het MT-dashboard (Joris, 23 sep): het bureau dit jaar, de laatste volle maand en
 * de lopende maand, plus een regel per functiegroep. Alle cijfers en de mensen erachter staan op
 * /declarabiliteit; zelfde bron en rekenregels (docs/declarabiliteit-plan.md).
 */
function Getal({ label, c, sub }) {
  const k = kleurVoor(c?.declarabiliteit);
  return (
    <Box sx={{ px: 2, py: 1.25, borderRadius: 2, bgcolor: k.bg, flex: '1 1 150px', minWidth: 150 }}>
      <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
      <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 24, color: k.fg, fontVariantNumeric: 'tabular-nums' }}>
        {c?.ondergrens && c?.declarabiliteit !== null ? '≥' : ''}{pct(c?.declarabiliteit)}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{sub}</Typography>
    </Box>
  );
}

export default function DeclarabiliteitBlok({ refreshKey }) {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);

  useEffect(() => {
    let actief = true;
    setFout(null);   // na een tijdelijke fout komt het blok bij verversen gewoon terug
    const nu = new Date();
    const jaar = nu.getFullYear();
    const maand = String(nu.getMonth() + 1).padStart(2, '0');
    haal(`${API}/maanden?van=${jaar}-01&tot=${jaar}-${maand}`)
      .then((d) => { if (actief) setData(d); })
      .catch((e) => { if (actief) setFout(e.message); });
    return () => { actief = false; };
  }, [refreshKey]);

  if (fout) return null;   // geen declarabiliteit (nog geen uren opgehaald of geen recht): blok weglaten
  if (!data) {
    return (
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }
  const lijst = data.maanden || [];
  const lopend = lijst[lijst.length - 1];
  const vorige = lijst.length > 1 ? lijst[lijst.length - 2] : null;
  const jaar = lopend ? lopend.slice(0, 4) : '';

  return (
    <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #e5e7eb' }} elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 16, flex: 1 }}>Declarabiliteit</Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>uren binnen budget ÷ beschikbare uren · norm {NORM}%</Typography>
        <Box component={RouterLink} to="/declarabiliteit" sx={{ fontSize: 13, fontWeight: 700, color: ACCENT.color, textDecoration: 'none' }}>
          Alle cijfers →
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Getal label={`Bureau ${jaar}`} c={data.totaal}
          sub={`${uren(data.totaal?.geschreven)} geschreven · ${uren(data.totaal?.boven)} boven budget`} />
        {vorige && (
          <Getal label={maandLabel(vorige)} c={data.bureau?.[vorige]}
            sub={`${pct(data.bureau?.[vorige]?.intern_pct)} intern · ${pct(bovenPct(data.bureau?.[vorige]))} boven`} />
        )}
        {lopend && (
          <Getal label={`${maandLabel(lopend)} t/m gisteren`} c={data.bureau?.[lopend]}
            sub={`${pct(data.bureau?.[lopend]?.intern_pct)} intern · ${pct(bovenPct(data.bureau?.[lopend]))} boven`} />
        )}
      </Box>

      {data.groepen?.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{
            width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums',
            '& th': { textAlign: 'right', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e5e7eb', py: 0.75, px: 1, whiteSpace: 'nowrap' },
            '& td': { textAlign: 'right', borderBottom: '1px solid #f1f5f9', py: 0.75, px: 1 },
            '& th:first-of-type, & td:first-of-type': { textAlign: 'left' },
          }}>
            <thead>
              <tr>
                <th>Functiegroep</th>
                <th>Declarabel {jaar}</th>
                {vorige && <th>{maandLabel(vorige)}</th>}
                <th>Intern</th>
                <th>Extern binnen budget</th>
                <th>Boven budget</th>
              </tr>
            </thead>
            <tbody>
              {data.groepen.map((g) => {
                const t = g.totaal || {};
                return (
                  <tr key={g.groep} title={g.leden.join(', ')}>
                    <td>
                      <strong>{g.groep}</strong>
                      <Box component="span" sx={{ color: '#94a3b8', ml: 0.75, fontSize: 12 }}>{g.leden.length}</Box>
                    </td>
                    <td style={{ color: kleurVoor(t.declarabiliteit).fg, fontWeight: 700 }}>
                      {t.ondergrens && t.declarabiliteit !== null ? '≥' : ''}{pct(t.declarabiliteit)}
                    </td>
                    {vorige && <td>{pct(g.maanden?.[vorige]?.declarabiliteit)}</td>}
                    <td>{pct(t.intern_pct)}</td>
                    <td>{pct(t.binnen_pct_van_extern)}</td>
                    <td style={{ color: (t.boven || 0) >= 0.5 ? ROOD : undefined }}>{uren(t.boven)} · {pct(bovenPct(t))}</td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
